import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, Card, Badge, Metric, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Star, ShieldAlert, ShieldCheck, Plus, Trash2, Upload, Download, Building2, ArrowRight, UserCircle, Power, PowerOff } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { useCurrency } from "@/lib/currency";
import { useIdTypes, idTypeLabel } from "@/hooks/useIdTypes";
import { GovtDocRow } from "@/components/guests/GovtDocRow";
import { GuestStayDocuments } from "@/components/documents/GuestStayDocuments";

const lookupCache = {};
async function loadLookup(key) {
  if (lookupCache[key]) return lookupCache[key];
  try {
    const { data } = await api.get(`/master/${key}/?page_size=500`);
    const map = {}; data.items.forEach(r => { map[r.code] = r.name; });
    lookupCache[key] = map; return map;
  } catch {
    // Label lookups are cosmetic — a perms gap (e.g. Front Desk officer without
    // master read) must never crash the guest profile. Degrade to raw codes.
    return {};
  }
}

function Section({ title, fields, data, onSave, editable = true, lookups, children, testid }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const save = async () => { await onSave(form); setEditing(false); };

  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono">{title}</div>
        {editable && !editing && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-6 px-2 text-[11px] text-hocp-textMuted hover:text-hocp-deepTeal" data-testid={`${testid}-edit`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>}
      </div>
      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 text-[13px]">
          {fields.map(f => {
            let v = data?.[f.key];
            if (f.lookup && v) v = lookups?.[f.lookup]?.[v] || v;
            return (
              <div key={f.key} className="flex justify-between gap-3 border-b border-hocp-border pb-2">
                <span className="text-hocp-textMuted text-[11px] uppercase tracking-wider mono">{f.label}</span>
                <span className={`text-hocp-textPrimary text-right ${f.mono ? "mono" : ""}`}>{v || <span className="text-zinc-700">—</span>}</span>
              </div>
            );
          })}
          {children}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map(f => (
              <div key={f.key}>
                <Label className="text-xs uppercase tracking-wider text-hocp-textMuted">{f.label}</Label>
                {f.lookup ? (
                  <Select value={form?.[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid={`${testid}-${f.key}`}><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent className="max-h-[260px]">{Object.entries(lookups[f.lookup] || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type || "text"} value={form?.[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={`bg-hocp-bgCard border-hocp-border mt-1.5 ${f.mono ? "mono" : ""}`} data-testid={`${testid}-${f.key}`}/>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => { setForm(data); setEditing(false); }} className="h-8">Cancel</Button>
            <Button size="sm" onClick={save} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-8" data-testid={`${testid}-save`}>Save</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function GuestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const money = useCurrency();
  const [g, setG] = useState(null);
  const [stays, setStays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [docs, setDocs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [lookups, setLookups] = useState({});
  const [docDialog, setDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ type: "aadhaar", number: "", issue_country: "", expiry_date: "" });
  const [idFiles, setIdFiles] = useState([]);
  const idTypes = useIdTypes();
  const [noteDraft, setNoteDraft] = useState("");
  const [vipDialog, setVipDialog] = useState(false);
  const [vipForm, setVipForm] = useState({ vip_status: "gold", reason: "" });
  const [blDialog, setBlDialog] = useState(false);
  const [blForm, setBlForm] = useState({ reason: "" });
  const [fileUpload, setFileUpload] = useState(null);

  const load = useCallback(async () => {
    try {
      const [gd, st, fs, dc, au] = await Promise.all([
        api.get(`/pms/guests/${id}`), api.get(`/pms/guests/${id}/stays`),
        api.get(`/pms/guests/${id}/financial-summary`), api.get(`/pms/guests/${id}/documents`),
        api.get(`/pms/guests/${id}/audit`),
      ]);
      setG(gd.data); setStays(st.data.items); setSummary(fs.data); setDocs(dc.data); setAudit(au.data);
      api.get(`/guest/v1/guests/${id}/documents`).then((r) => setIdFiles((r.data || []).filter((d) => d.collection_method))).catch(() => setIdFiles([]));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);

  useEffect(() => { (async () => {
    const [countries, titles, genders, marital, classes, docTypes, occupations] = await Promise.all([
      loadLookup("countries"), loadLookup("titles"), loadLookup("genders"),
      loadLookup("marital-statuses"), loadLookup("guest-classifications"),
      loadLookup("id-document-types"), loadLookup("occupations"),
    ]);
    setLookups({ countries, titles, genders, "marital-statuses": marital, "guest-classifications": classes, "id-document-types": docTypes, occupations });
  })(); load(); }, [load]);

  if (!g) return <div className="text-hocp-textMuted text-sm">Loading…</div>;

  const fullName = [lookups.titles?.[g.title_code], g.first_name, g.middle_name, g.last_name].filter(Boolean).join(" ");
  const saveSection = (section) => async (form) => {
    try { await api.patch(`/pms/guests/${id}/section/${section}`, form); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addGovtDoc = async () => {
    try { await api.post(`/pms/guests/${id}/government-documents`, docForm); toast.success("Document added"); setDocDialog(false); setDocForm({ type: "aadhaar", number: "", issue_country: "", expiry_date: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeGovtDoc = async (idx) => {
    if (!window.confirm("Remove this document?")) return;
    try { await api.delete(`/pms/guests/${id}/government-documents/${idx}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const addNote = async () => {
    if (!noteDraft.trim()) return;
    try { await api.post(`/pms/guests/${id}/notes`, { comment: noteDraft, visibility: "internal" }); setNoteDraft(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const assignVip = async () => {
    try { await api.post(`/pms/guests/${id}/vip`, vipForm); toast.success("VIP updated"); setVipDialog(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const blacklist = async () => {
    try { const { data } = await api.post(`/pms/guests/${id}/blacklist`, blForm); toast.success(data.workflow_instance_id ? "Blacklist approval requested" : "Blacklisted"); setBlDialog(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const liftBl = async () => {
    if (!window.confirm("Lift blacklist?")) return;
    try { await api.post(`/pms/guests/${id}/blacklist/lift`, { reason: "Lifted by admin" }); toast.success("Lifted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async () => {
    try { await api.post(`/pms/guests/${id}/${g.is_active ? "deactivate" : "restore"}`, { reason: "from profile" }); toast.success(g.is_active ? "Deactivated" : "Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadDoc = async () => {
    if (!fileUpload) return;
    const fd = new FormData(); fd.append("file", fileUpload); fd.append("entity_type", "guest"); fd.append("entity_id", id);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); setFileUpload(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="guest-detail-page">
      <PageHeader title={fullName || "Guest"} subtitle={`Guest · ${g.code}`}
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setVipDialog(true)} className="h-9 bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="action-assign-vip"><Star className="w-3.5 h-3.5 mr-1.5"/>Assign VIP</Button>
            {g.is_blacklisted ? (
              <Button variant="outline" onClick={liftBl} className="h-9 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="action-lift-blacklist"><ShieldCheck className="w-3.5 h-3.5 mr-1.5"/>Lift blacklist</Button>
            ) : (
              <Button variant="outline" onClick={() => setBlDialog(true)} className="h-9 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="action-blacklist"><ShieldAlert className="w-3.5 h-3.5 mr-1.5"/>Blacklist</Button>
            )}
            <Button variant="outline" onClick={toggleActive} className="h-9 bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="action-toggle-active">{g.is_active ? <><PowerOff className="w-3.5 h-3.5 mr-1.5"/>Deactivate</> : <><Power className="w-3.5 h-3.5 mr-1.5"/>Restore</>}</Button>
            <Button variant="outline" onClick={() => nav("/pms/guests")} className="h-9 bg-transparent border-hocp-border hover:bg-hocp-bgSubtle">Back</Button>
          </div>
        }
      />

      {/* Header chip strip */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-4">
        <Card className="p-5 lg:col-span-1 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-hocp-bgSubtle border border-hocp-border flex items-center justify-center mb-2"><UserCircle className="w-8 h-8 text-hocp-textMuted" strokeWidth={1.25}/></div>
          <div className="font-heading text-[14px] text-white">{fullName}</div>
          <div className="mono text-[10px] text-hocp-textMuted mt-0.5">{g.code}</div>
          <div className="flex gap-1.5 mt-2 flex-wrap justify-center">
            {g.vip_status !== "none" && <Badge tone="warning"><Star className="w-2.5 h-2.5 mr-1"/>{g.vip_status?.toUpperCase()}</Badge>}
            {g.is_blacklisted && <Badge tone="error">BLACKLISTED</Badge>}
            {g.special_flags?.is_corporate && <Badge tone="info">CORPORATE</Badge>}
            {g.special_flags?.is_frequent && <Badge tone="info">FREQUENT</Badge>}
            {g.special_flags?.do_not_disturb && <Badge tone="warning">DND</Badge>}
            {!g.is_active && <Badge tone="neutral">INACTIVE</Badge>}
          </div>
        </Card>
        <Metric label="Total Stays" value={summary?.folios ?? g.total_stays ?? 0} testid="guest-stays-count"/>
        <Metric label="Charges" value={money(summary?.total_charges || 0)} testid="guest-charges"/>
        <Metric label="Payments" value={money(summary?.total_payments || 0)} testid="guest-payments"/>
        <Metric label="Outstanding" value={money(summary?.outstanding_balance || 0)} testid="guest-outstanding"/>
      </div>

      <Tabs defaultValue="general" className="space-y-3">
        <TabsList className="bg-hocp-bgSubtle border border-hocp-border overflow-x-auto flex-wrap">
          {["general", "contact", "documents", "preferences", "classification", "emergency", "notes", "timeline", "audit"].map(t =>
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="general">
          <Section title="General" testid="section-general" lookups={lookups}
            fields={[
              { key: "title_code", label: "Title", lookup: "titles" },
              { key: "first_name", label: "First name" },
              { key: "middle_name", label: "Middle name" },
              { key: "last_name", label: "Last name" },
              { key: "preferred_name", label: "Preferred name" },
              { key: "gender_code", label: "Gender", lookup: "genders" },
              { key: "dob", label: "DOB", type: "date", mono: true },
              { key: "nationality_code", label: "Nationality", lookup: "countries" },
              { key: "occupation", label: "Occupation" },
              { key: "marital_status_code", label: "Marital Status", lookup: "marital-statuses" },
            ]} data={g} onSave={saveSection("general")}/>
        </TabsContent>

        <TabsContent value="contact">
          <Section title="Contact" testid="section-contact" lookups={lookups}
            fields={[
              { key: "primary_phone", label: "Mobile", mono: true },
              { key: "alternate_mobile", label: "Alt mobile", mono: true },
              { key: "email", label: "Email" },
              { key: "whatsapp", label: "WhatsApp", mono: true },
              { key: "address", label: "Address" },
              { key: "city", label: "City" },
              { key: "state", label: "State" },
              { key: "country_code", label: "Country", lookup: "countries" },
              { key: "postal_code", label: "Postal", mono: true },
            ]} data={g} onSave={saveSection("contact")}/>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono">Government Documents</div>
              <Button size="sm" onClick={() => setDocDialog(true)} className="h-7 bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold text-[12px]" data-testid="add-govt-doc-button"><Plus className="w-3 h-3 mr-1.5"/>Add</Button>
            </div>
            {(g.government_documents || []).length === 0 && idFiles.length === 0 ? <div className="text-hocp-textMuted text-xs">No government documents.</div> : (
              <div className="space-y-2">
                {(g.government_documents || []).map((d, i) => (
                  <GovtDocRow key={d.id || i} guestId={id} doc={d} docId={d.id || String(i)} testid={`govt-doc-${i}`} onChanged={load} onDelete={() => removeGovtDoc(i)}
                    label={`${idTypeLabel(d.type, idTypes)}${d.issue_country ? ` · ${lookups.countries?.[d.issue_country] || d.issue_country}` : ""}`} />
                ))}
                {idFiles.map((d, i) => (
                  <GovtDocRow key={d.id} guestId={id} doc={d} docId={d.id} testid={`govt-file-${i}`} onChanged={load}
                    label={`${idTypeLabel(d.doc_type, idTypes)}${d.collection_method === "marked_collected" ? " · seen at desk" : " · uploaded"}`} />
                ))}
              </div>
            )}
            <div className="mt-6 pt-4 border-t border-hocp-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono">Uploaded Documents ({docs.length})</div>
                <div className="flex gap-2 items-center">
                  <Input type="file" onChange={(e) => setFileUpload(e.target.files?.[0])} className="bg-hocp-bgCard border-hocp-border h-8 text-[12px] file:text-hocp-textPrimary file:bg-transparent file:border-0 file:text-xs w-[280px]" data-testid="doc-upload-input"/>
                  <Button size="sm" onClick={uploadDoc} disabled={!fileUpload} className="h-8 bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold text-[12px]" data-testid="upload-doc-button"><Upload className="w-3 h-3 mr-1"/>Upload</Button>
                </div>
              </div>
              {docs.length === 0 ? <div className="text-hocp-textMuted text-xs">No uploaded documents.</div> :
                <div className="space-y-1.5">{docs.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 border border-hocp-border rounded text-[12px]">
                    <span className="text-hocp-textPrimary">{d.filename}</span>
                    <div className="flex items-center gap-2"><span className="text-hocp-textMuted mono text-[10px]">v{d.version} · {(d.size/1024).toFixed(1)} KB</span>
                      <a href={`${API_BASE}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-hocp-textMuted hover:text-hocp-deepTeal"><Download className="w-3.5 h-3.5"/></a>
                    </div>
                  </div>
                ))}</div>
              }
            </div>
            <div className="mt-6 pt-4 border-t border-hocp-border" data-testid="guest-stay-documents-section">
              <div className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-3">Stay Documents</div>
              <GuestStayDocuments guestId={id} stays={stays} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="preferences">
          <Section title="Preferences" testid="section-preferences" lookups={lookups}
            fields={[]} data={g.preferences || {}} onSave={async (form) => {
              try { await api.patch(`/pms/guests/${id}/section/preferences`, { preferences: form }); toast.success("Saved"); load(); }
              catch (e) { toast.error(formatApiError(e)); }
            }}
          >
            <pre className="mono text-[11px] text-hocp-textMuted bg-hocp-bgCard border border-hocp-border rounded p-3 col-span-2 max-h-[280px] overflow-y-auto">{JSON.stringify(g.preferences || {}, null, 2)}</pre>
          </Section>
        </TabsContent>

        <TabsContent value="classification">
          <Section title="Classification & Special Flags" testid="section-classification" lookups={lookups}
            fields={[{ key: "classification_code", label: "Classification", lookup: "guest-classifications" }]}
            data={g} onSave={saveSection("general")}/>
          <Card className="p-5 mt-3">
            <div className="text-[11px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-3">Special Flags</div>
            <div className="space-y-2 text-[13px]">
              {[["is_corporate", "Corporate"], ["is_frequent", "Frequent guest"], ["do_not_disturb", "Do not disturb"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between border-b border-hocp-border pb-2">
                  <span className="text-hocp-textMuted">{label}</span>
                  <Switch checked={g.special_flags?.[k] || false} onCheckedChange={async (v) => {
                    try { await api.patch(`/pms/guests/${id}/section/flags`, { special_flags: { ...(g.special_flags||{}), [k]: v } }); load(); }
                    catch (e) { toast.error(formatApiError(e)); }
                  }} data-testid={`flag-${k}`}/>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="emergency">
          <Section title="Emergency Contact" testid="section-emergency" lookups={lookups}
            fields={[
              { key: "name", label: "Name" }, { key: "relationship", label: "Relationship" },
              { key: "mobile", label: "Mobile", mono: true }, { key: "email", label: "Email" },
            ]} data={g.emergency_contact || {}}
            onSave={async (form) => { try { await api.patch(`/pms/guests/${id}/section/emergency`, { emergency_contact: form }); toast.success("Saved"); load(); } catch (e) { toast.error(formatApiError(e)); } }}
          />
        </TabsContent>

        <TabsContent value="notes">
          <Card className="p-5">
            <div className="flex gap-2 mb-3">
              <Textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Add internal note…" aria-label="Add internal note" rows={2} className="bg-hocp-bgCard border-hocp-border" data-testid="note-input"/>
              <Button onClick={addNote} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9" data-testid="add-note-button"><Plus className="w-3.5 h-3.5 mr-1.5"/>Add</Button>
            </div>
            <div className="space-y-1.5">
              {(g.notes_internal || []).length === 0 ? <div className="text-xs text-hocp-textMuted">No notes yet.</div> :
                g.notes_internal.map((n, i) => (
                  <div key={n.id || i} className="p-3 border border-hocp-border rounded bg-hocp-bgCard" data-testid={`note-${i}`}>
                    <div className="flex justify-between text-[10px] mono text-hocp-textMuted mb-1">
                      <span>{n.by_email || "system"}</span><span>{new Date(n.at).toLocaleString()}</span>
                    </div>
                    <div className="text-[12px] text-hocp-textPrimary">{n.comment}</div>
                  </div>
                ))
              }
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <div className="space-y-1.5">
            {(g.timeline || []).slice().reverse().map((t, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-hocp-border rounded-md bg-hocp-bgCard">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2"></div>
                <div className="flex-1">
                  <div className="flex justify-between gap-2"><span className="mono text-[12px] text-hocp-textPrimary">{t.event}</span><span className="mono text-[10px] text-hocp-textTertiary">{new Date(t.at).toLocaleString()}</span></div>
                  {t.note && <div className="text-[11px] text-hocp-textMuted mt-0.5">{t.note}</div>}
                  <div className="text-[10px] text-hocp-textTertiary mt-0.5">by {t.by}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="border border-hocp-border rounded bg-hocp-bgCard overflow-hidden">
            <table className="w-full"><thead><tr className="border-b border-hocp-border bg-hocp-bgSubtle">{["Time", "Action", "Actor"].map(h => <th key={h} className="px-3 py-2.5 text-[10px] mono uppercase tracking-wider text-hocp-textMuted text-left">{h}</th>)}</tr></thead>
              <tbody>{audit.map(a => (
                <tr key={a.id} className="border-b border-hocp-border last:border-0">
                  <td className="px-3 py-2.5 text-[10px] mono text-hocp-textTertiary">{new Date(a.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2.5"><Badge tone="info">{a.action}</Badge></td>
                  <td className="px-3 py-2.5 mono text-[11px] text-hocp-textMuted">{a.actor_email}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add govt doc */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Add Government Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Type *</Label>
              <Select value={docForm.type} onValueChange={(v) => setDocForm({ ...docForm, type: v })}>
                <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="doc-type"><SelectValue/></SelectTrigger>
                <SelectContent>{idTypes.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`doc-type-${t.value}`}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Number *</Label><Input value={docForm.number} onChange={(e) => setDocForm({ ...docForm, number: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="doc-number"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Issue country</Label>
                <Select value={docForm.issue_country} onValueChange={(v) => setDocForm({ ...docForm, issue_country: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>{Object.entries(lookups.countries || {}).map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Expiry</Label><DatePicker value={(docForm.expiry_date || "" || '').slice(0,10)} onChange={(_v) => setDocForm({ ...docForm, expiry_date: _v })}/></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addGovtDoc} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-govt-doc">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIP */}
      <Dialog open={vipDialog} onOpenChange={setVipDialog}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Assign VIP</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">VIP status</Label>
              <Select value={vipForm.vip_status} onValueChange={(v) => setVipForm({ ...vipForm, vip_status: v })}>
                <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="vip-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>{["none", "bronze", "silver", "gold", "platinum"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Reason</Label><Input value={vipForm.reason} onChange={(e) => setVipForm({ ...vipForm, reason: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5"/></div>
          </div>
          <DialogFooter><Button onClick={assignVip} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-vip">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blacklist */}
      <Dialog open={blDialog} onOpenChange={setBlDialog}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Request Blacklist (Approval Workflow)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-hocp-textMuted">This will start a GUEST_BLACKLIST_APPROVAL workflow. Guest is not blacklisted until approved.</div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Reason *</Label><Textarea value={blForm.reason} onChange={(e) => setBlForm({ ...blForm, reason: e.target.value })} rows={3} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="blacklist-reason"/></div>
          </div>
          <DialogFooter><Button onClick={blacklist} disabled={!blForm.reason} className="bg-red-500 hover:bg-red-600 text-white" data-testid="submit-blacklist">Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
